// Capture graph: mic (getUserMedia) + optional tab/system audio
// (getDisplayMedia) → gain-balanced mix → downsampler worklet.
// Platform caveats: docs/design-docs/audio-capture.md

import { micConstraints } from "../lib/devices";
import { DOWNSAMPLER_NAME, loadDownsampler } from "./worklet";

export interface MixerCallbacks {
  /** 16 kHz mono frames from the worklet. */
  onFrame: (frame: Float32Array) => void;
  /** User stopped sharing the tab — we downgrade to mic-only. */
  onSystemEnded: () => void;
}

export class CaptureMixer {
  private ctx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private sysStream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;

  async start(micDeviceId: string | undefined, cb: MixerCallbacks): Promise<void> {
    // Honor the saved device prefs (Settings → Devices); an explicit
    // recorder selection still overrides the stored microphone.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: micConstraints(micDeviceId),
    });
    await this.startWithStream(stream, cb, true);
  }

  /// Hosted calls (plan 0002): tap an existing mic stream (the one
  /// LiveKit publishes) instead of opening a second capture. The
  /// caller owns the stream's lifecycle.
  async startWithStream(
    stream: MediaStream,
    cb: MixerCallbacks,
    ownStream = false,
  ): Promise<void> {
    this.micStream = ownStream ? stream : null;
    const sourceStream = stream;
    this.ctx = new AudioContext();
    await loadDownsampler(this.ctx);
    this.node = new AudioWorkletNode(this.ctx, DOWNSAMPLER_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 0,
    });
    this.node.port.onmessage = (ev: MessageEvent<Float32Array>) => cb.onFrame(ev.data);

    const micSource = this.ctx.createMediaStreamSource(sourceStream);
    const micGain = this.ctx.createGain();
    micGain.gain.value = 1.0;
    micSource.connect(micGain).connect(this.node);
    this.systemEndedCb = cb.onSystemEnded;
  }

  private systemEndedCb: (() => void) | null = null;

  /** Prompt the share picker; the user must tick "share audio". */
  async addSystemAudio(): Promise<boolean> {
    if (!this.ctx || !this.node) throw new Error("mixer not started");
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true, // required by the API; we drop the track
      audio: true,
    });
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      stream.getTracks().forEach((t) => t.stop());
      return false; // user didn't share audio
    }
    // We only want audio; stop video to save CPU.
    stream.getVideoTracks().forEach((t) => t.stop());
    this.sysStream = stream;
    const src = this.ctx.createMediaStreamSource(new MediaStream(audioTracks));
    const gain = this.ctx.createGain();
    gain.gain.value = 0.8; // keep mic on top of system audio
    src.connect(gain).connect(this.node);
    const track = audioTracks[0];
    if (track) {
      track.onended = () => {
        this.sysStream = null;
        this.systemEndedCb?.();
      };
    }
    return true;
  }

  hasSystemAudio(): boolean {
    return this.sysStream !== null;
  }

  async stop(): Promise<void> {
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.sysStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.sysStream = null;
    if (this.node) this.node.port.onmessage = null;
    this.node = null;
    if (this.ctx && this.ctx.state !== "closed") {
      await this.ctx.close();
    }
    this.ctx = null;
  }
}

export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === "audioinput");
}
